import { type ReactNode, useCallback, useEffect, useState } from 'react'

import { MeetingErrorLayout } from 'modules/meetings/components/Layout/MeetingErrorLayout'

import { MeetingsMeetingContext, type MeetingsMeetingContextState } from '../MeetingsMeetingContext'
import { useAutosave } from './hooks/useAutosave'
import { useMeetingData } from './hooks/useMeetingData'

interface MeetingsMeetingProviderProps {
  children: ReactNode
}

export const MeetingsMeetingProvider = ({ children }: MeetingsMeetingProviderProps) => {
  const [error, setError] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [notes, setNotesState] = useState<string | null>(null)

  // Custom hooks for different concerns
  const {
    meeting,
    page,
    notes: initialNotes,
    loading,
    error: meetingDataError,
    errorMessage: meetingDataErrorMessage,
    resetError: resetMeetingDataError,
  } = useMeetingData()

  const handleError = useCallback((hasError: boolean, message: string | null) => {
    setError(hasError)
    setErrorMessage(message)
  }, [])

  const { isSavingNotes, setNotes, getCleanup } = useAutosave({
    page,
    onError: handleError,
  })

  // Sync initial data with local state
  useEffect(() => {
    if (initialNotes !== null) {
      setNotesState(initialNotes)
    }
  }, [initialNotes])

  // Handle notes changes
  const handleNotesChange = useCallback(
    (json: string) => {
      try {
        JSON.parse(json)
        setNotesState(json)
        setNotes(json)
      } catch (e) {
        setNotesState('')
      }
    },
    [setNotes]
  )

  // Combined error state
  const hasError = error || meetingDataError
  const combinedErrorMessage = errorMessage || meetingDataErrorMessage

  const resetError = useCallback(() => {
    setError(false)
    setErrorMessage(null)
    resetMeetingDataError()
  }, [resetMeetingDataError])

  // Cleanup autosave timeout on unmount
  useEffect(() => {
    const cleanup = getCleanup()
    return cleanup
  }, [getCleanup])

  const value: MeetingsMeetingContextState = {
    notes,
    setNotes: handleNotesChange,
    loading,
    isSavingNotes,
    error: hasError,
    errorMessage: combinedErrorMessage,
    resetError,
    page,
    meeting,
  }

  if (hasError) {
    return <MeetingErrorLayout />
  }

  return <MeetingsMeetingContext.Provider value={value}>{children}</MeetingsMeetingContext.Provider>
}
